import React, { useCallback, useMemo, useState } from 'react';
import { Button, Flex, ProviderConsumer, Text } from '@fluentui/react-northstar';
import { platformService } from '../shared/services/platform.service';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import {
  DEFAULT_API_STATUS,
  DONE,
  ERROR,
  LOADING,
  SETTINGS,
  TREE_LISTING_CONSTANTS,
  SETTINGS_PAGES,
  ENDPOINT_SECURITY_MAPPINGS,
  GETTING_ALL_TEAMS,
  TEAMS,
  NEW_TEAM,
  TEAMS_HEADERS,
  UNAUTHORIZED_ACCESS,
  SOMETHING_WENT_WRONG,
  CONTACT_ADMIN,
  REFRESH_SESSION,
  LICENSE_FEATURE
} from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { KBListWrapper } from '../kb/KBListWrapper';
import { useHistory } from 'react-router-dom';
import { CheckLogin } from '../shared/components/CheckLogin';
import { Helmet } from 'react-helmet';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { Toolbar } from '../shared/components/Toolbar';
import ConfigCSS from '../config/config.module.css';
import { ITeam } from '../shared/interfaces/supportGroup.interface';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import useCurrentPage from '../kb/useCurrentPage';
import { useTranslation } from 'react-i18next';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { MoreTableActions } from '../shared/components/table/TableWrapper';
import { getSortableTextValue, TABLE_SORT_META, TableSortColumn, TableSortConfig } from '../shared/components/table/tableSortConfig';


export const TeamsTable = () => {
  const {t }= useTranslation();
  const history = useHistory();
  const api = new platformService();
  const [data, setData] = useState<RowsData[]>([]);
  const [apiStatus, setApiStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const [multiEmailInbox, setMultiEmailInbox] = useState(false);
  const {currentPage, setCurrentPage} = useCurrentPage()

  const routeToTeamsPage = (team: ITeam) => {
    history.push(`/settings/teams?id=${team.Id}`);
  }

  const fetchData = useCallback(async () => {
    setApiStatus({ ...apiStatus, msg: GETTING_ALL_TEAMS });

    const check = ENDPOINT_SECURITY_MAPPINGS.find(x => x.key == SETTINGS_PAGES.TEAMS);
    if (check && JSON.parse(sessionStorage.getItem('my_application_roles') || '[]').filter(x => x.EndpointPath == check.path && x.Permissions.includes(check.method)).length <= 0) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: 403
      });
      return;
    }

    try {
      const isMultiInbox = await getCachedFeature(LICENSE_FEATURE.MultiEmailInbox);
      setMultiEmailInbox(isMultiInbox);

      const { data: { value } } = await api.getTeams('?$orderby=FriendlyName');
      const rowData: RowsData[] = value.map((team: ITeam) => ({
        id: team['Id'],
        [t('teams.teams-columns.name-search')]: team.FriendlyName,
        [t('teams.teams-columns.friendly-name')]: (<Flex gap="gap.medium" vAlign="center">
          <Text content={team.FriendlyName} />
          {team.IsDefault && (
            <Text content={TREE_LISTING_CONSTANTS.default} size="smaller" weight="light" disabled />
          )}
        </Flex>
        ),
        [t('teams.teams-columns.team')]: team.Name,
        [t('teams.teams-columns.triage-channel')]: team.TeamsChannelName,
        [t('teams.teams-columns.description')]: team.Description,
        [t('teams.teams-columns.email')]: team.EmailAddress,
        [t('ticket.ticket-list.created-date')]: team.CreatedDate ? new Date(team.CreatedDate).toLocaleString() : '',
        [TABLE_SORT_META.createdDateIso]: team.CreatedDate ?? '',
        [t('ticket.ticket-list.modified-date')]: team.ModifiedDate ? new Date(team.ModifiedDate).toLocaleString() : '',
        [TABLE_SORT_META.modifiedDateIso]: team.ModifiedDate ?? '',
        "": <MoreTableActions t={t} moreEditAction={() => { routeToTeamsPage(team) }}/>
      }));

      setData(rowData);
      setApiStatus({ ...apiStatus, status: DONE });
    } catch (err: any) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: err.response.status
      });
    }
  }, [t]);

  const teamsSortConfig = useMemo((): TableSortConfig<string> => {
    const friendlyKey = t('teams.teams-columns.friendly-name');
    const teamKey = t('teams.teams-columns.team');
    const groupChannelKey = t('teams.teams-columns.triage-channel');
    const emailKey = t('teams.teams-columns.email');
    const descriptionKey = t('teams.teams-columns.description');
    const createdDateKey = t('ticket.ticket-list.created-date');
    const modifiedDateKey = t('ticket.ticket-list.modified-date');
    const nameSearchKey = t('teams.teams-columns.name-search');
    const base: TableSortColumn<string>[] = [
      {
        field: 'friendlyName',
        label: friendlyKey,
        headerKey: friendlyKey,
        getSortValue: (row) => getSortableTextValue(row[nameSearchKey]),
      },
      {
        field: 'team',
        label: teamKey,
        headerKey: teamKey,
        getSortValue: (row) => getSortableTextValue(row[teamKey]),
      },
      {
        field: 'groupChannel',
        label: groupChannelKey,
        headerKey: groupChannelKey,
        getSortValue: (row) => getSortableTextValue(row[groupChannelKey]),
      },
    ];
    if (multiEmailInbox) {
      base.push({
        field: 'connectedMailbox',
        label: emailKey,
        headerKey: emailKey,
        getSortValue: (row) => getSortableTextValue(row[emailKey]),
      });
    }
    base.push({
      field: 'description',
      label: descriptionKey,
      headerKey: descriptionKey,
      getSortValue: (row) => getSortableTextValue(row[descriptionKey]),
    });
    base.push(
      {
        field: 'createdDate',
        label: createdDateKey,
        headerKey: createdDateKey,
        getSortValue: (row) => getSortableTextValue(row[TABLE_SORT_META.createdDateIso]),
      },
      {
        field: 'modifiedDate',
        label: modifiedDateKey,
        headerKey: modifiedDateKey,
        getSortValue: (row) => getSortableTextValue(row[TABLE_SORT_META.modifiedDateIso]),
      }
    );
    return {
      storageKey: 'settingsTeamsTableSort',
      defaultSort: { sortField: 'friendlyName', sortDirection: 'asc' },
      columns: base,
    };
  }, [t, multiEmailInbox]);

  const loadPage = () => {
    fetchData();
  }

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: `/${SETTINGS}`
      },
      {
        title: toTitleCase(TEAMS),
        link: ''
      }
    ]
  };

  const errConfig = {
    fields: {
      title:
        apiStatus.errCode === 401 || apiStatus.errCode === 403
          ? UNAUTHORIZED_ACCESS
          : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  };

  return (
    <CheckLogin onSignedIn={loadPage}>
      <Helmet>
        <title>{toTitleCase(TEAMS)} - Tikit</title>
      </Helmet>
      {
        apiStatus.status === LOADING ? (
          <LoadingScreen message={apiStatus.msg} />
        ) : 
        (<>
          {apiStatus.status === ERROR && (
          <>
            {apiStatus.errCode != 402 && (
              <ProviderConsumer render={(globalTheme) => (
              <>
                <Toolbar globalTheme={globalTheme} title="Settings > Teams" hideSave={true} breadcrumbs={navs}/>
                <Flex className={ConfigCSS.configBody}>
                  <Flex.Item grow>
                    <>
                      <Communication {...errConfig} />
                      {apiStatus.errCode === 401 && (<Button content={REFRESH_SESSION} primary onClick={_e => { Providers.globalProvider.login(); }} />)}
                    </>
                  </Flex.Item>
                </Flex>
              </>)} />
            )}
            {apiStatus.errCode === 402 && (
              <UpgradeNeeded
                toolbarText={`${toTitleCase(SETTINGS)} > ${toTitleCase(TEAMS)}`}
                breadcrumbs={navs}
                headerText={t('teams.you-do-not-have-access')}
                subheaderText={t('teams.please-upgrade-your-plan')}
              />
            )}
          </>
          )} 
          {apiStatus.status != ERROR && (
            <KBListWrapper
              title={toTitleCase(TEAMS)}
              addNewTitle={toTitleCase(NEW_TEAM)}
              routeName={SETTINGS_PAGES.TEAMS}
              headers={multiEmailInbox ? TEAMS_HEADERS : TEAMS_HEADERS.map((e, i) => i !== 3 ? e : undefined).filter(x => x !== undefined)}
              rows={data}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              breadcrumbs={navs}
              sortConfig={teamsSortConfig}
              ifEmpty={{
                header: t('teams.there-are-no-teams'),
                subHeader:
                t('teams.get-started-by-clicking')
              }}
            />
          )}
        </>
        )
      }
    </CheckLogin>
  )
};
